/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Checkbox } from "@octopusdeploy/design-system-components";
import type { EnvironmentResource, LifecycleResource } from "@octopusdeploy/octopus-server-client";
import { DeployLatestReleaseToEnvironmentActionResource } from "@octopusdeploy/octopus-server-client";
import { compact } from "lodash";
import * as React from "react";
import { EnvironmentChip, ChipIcon } from "~/components/Chips";
import LookupResourceChipComponent from "~/components/LookupResourceChip";
import { ExpandableFormSection, Summary } from "~/components/form";
import EnvironmentSelect from "~/components/form/EnvironmentSelect/EnvironmentSelect";
interface DeployLatestReleaseToEnvironmentActionEditorProps {
    action: DeployLatestReleaseToEnvironmentActionResource;
    allEnvironments: EnvironmentResource[];
    lifecycle: LifecycleResource;
    onActionChange(action: DeployLatestReleaseToEnvironmentActionResource): void;
}
interface DeployLatestReleaseToEnvironmentActionEditorState {
    environmentId: string;
    shouldRedeployWhenReleaseIsCurrent: boolean;
}
export class DeployLatestReleaseToEnvironmentActionEditor extends React.Component<DeployLatestReleaseToEnvironmentActionEditorProps, DeployLatestReleaseToEnvironmentActionEditorState> {
    firstPhaseEnvironments: EnvironmentResource[] = [];
    constructor(props: DeployLatestReleaseToEnvironmentActionEditorProps) {
        super(props);
        this.state = this.initState(this.props.action);
    }
    //eslint-disable-next-line react/no-unsafe
    UNSAFE_componentWillReceiveProps(newProps: DeployLatestReleaseToEnvironmentActionEditorProps) {
        const environmentChanged = this.props.action.EnvironmentId !== newProps.action.EnvironmentId;
        if (environmentChanged) {
            this.setState(this.initState(newProps.action));
        }
        const lifecycleChanged = this.props.action.ChannelId !== newProps.action.ChannelId || this.props.lifecycle.Id !== newProps.lifecycle.Id;
        this.initEnvironments(newProps.lifecycle, newProps.allEnvironments, lifecycleChanged);
    }
    render() {
        return (<div>
                <ExpandableFormSection errorKey="DestinationEnvironment" title="Destination environment" focusOnExpandAll summary={this.buildDestinationEnvironmentSummary()} help="The environment to deploy the latest release to. Only environments from the first phase of the lifecycle are available.">
                    <EnvironmentSelect allowClear={true} environments={this.firstPhaseEnvironments} value={this.state.environmentId} onChange={this.onDestinationEnvironmentChange}/>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="Redeploy" title="Re-deploy" summary={this.state.shouldRedeployWhenReleaseIsCurrent
                ? Summary.default("Re-deploy latest release to destination environment even if already up-to-date")
                : Summary.summary("Do not re-deploy latest release to destination environment if already up-to-date")} help="Choose whether Octopus should re-deploy latest release to destination environment if it is already up-to-date">
                    <Checkbox label="Re-deploy" value={this.state.shouldRedeployWhenReleaseIsCurrent} onChange={this.onShouldRedeployChange}/>
                </ExpandableFormSection>
            </div>);
    }
    initState(value?: DeployLatestReleaseToEnvironmentActionResource) {
        const action = value || new DeployLatestReleaseToEnvironmentActionResource();
        return {
            environmentId: action.EnvironmentId,
            shouldRedeployWhenReleaseIsCurrent: action.ShouldRedeployWhenReleaseIsCurrent,
        };
    }
    private environmentChip = (id: string) => {
        const LookupEnvironmentChip = LookupResourceChipComponent<EnvironmentResource>();
        return <LookupEnvironmentChip lookupCollection={this.props.allEnvironments} key={String(id)} lookupId={id} type={ChipIcon.Environment} chipRender={(item) => <EnvironmentChip environmentName={item.Name}/>}/>;
    };
    private buildDestinationEnvironmentSummary = () => {
        return this.state.environmentId ? Summary.summary(<span>Latest release will be deployed to the {this.environmentChip(this.state.environmentId)} environment</span>) : Summary.placeholder("No destination environment selected");
    };
    private onDestinationEnvironmentChange = (environmentId: string | undefined) => {
        this.setState({
            environmentId: environmentId!,
        }, () => this.raiseChange());
    };
    private onShouldRedeployChange = (shouldRedeployWhenReleaseIsCurrent: boolean) => {
        this.setState({
            shouldRedeployWhenReleaseIsCurrent,
        }, () => this.raiseChange());
    };
    private raiseChange = () => {
        this.props.onActionChange({
            ...this.props.action,
            EnvironmentId: this.state.environmentId,
            ShouldRedeployWhenReleaseIsCurrent: this.state.shouldRedeployWhenReleaseIsCurrent,
        });
    };
    private initEnvironments(lifecycle: LifecycleResource, allEnvironments: EnvironmentResource[], lifecycleChanged: boolean) {
        if (lifecycleChanged || this.firstPhaseEnvironments.length === 0) {
            // Get environments from the first phase of the lifecycle only
            if (lifecycle && lifecycle.Phases && lifecycle.Phases.length > 0) {
                const firstPhase = lifecycle.Phases[0];
                const firstPhaseEnvironmentIds = [...firstPhase.AutomaticDeploymentTargets, ...firstPhase.OptionalDeploymentTargets];
                this.firstPhaseEnvironments = this.filterToAccessibleEnvironments(firstPhaseEnvironmentIds, allEnvironments);
                // If only one environment is available, select it by default
                if (!this.state.environmentId && this.firstPhaseEnvironments.length === 1) {
                    this.setState({ environmentId: this.firstPhaseEnvironments[0].Id }, () => this.raiseChange());
                }
                if (lifecycleChanged) {
                    // Check if current environment is still valid in the new lifecycle
                    let environmentId = this.state.environmentId;
                    let shouldRaiseChange = false;
                    // If no environment is selected and only one is available, select it
                    if (!environmentId && this.firstPhaseEnvironments.length === 1) {
                        environmentId = this.firstPhaseEnvironments[0].Id;
                        shouldRaiseChange = true;
                    }
                    else if (environmentId && !this.firstPhaseEnvironments.some((e) => e.Id === environmentId)) {
                        environmentId = undefined!;
                        shouldRaiseChange = true;
                    }
                    if (shouldRaiseChange) {
                        this.setState({
                            environmentId,
                        }, () => this.raiseChange());
                    }
                }
            }
            else {
                this.firstPhaseEnvironments = [];
            }
        }
    }
    private filterToAccessibleEnvironments = (environmentIds: string[], allEnvironments: EnvironmentResource[]) => {
        return compact(environmentIds.map((envId: string) => allEnvironments.find((e) => e.Id === envId)).filter((e) => e));
    };
    static displayName = "DeployLatestReleaseToEnvironmentActionEditor";
}
